import { Box, Grid, Typography, Tooltip } from '@mui/material';

import { useState, useEffect, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CommentIcon } from 'common/assets/images/risk-gpt.svg';
import { Loader } from 'common/components/Loader/Loader';
import SelectDropdown from 'common/components/Select/SelectDropdown';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	KNOWLEDGE_BASE_OPTION_VALUE,
	TAB_VALUES,
	KNOWLEDGE_BASE_ERROR,
	KNOWLEDGE_BASE_VALUE,
} from 'common/constants/AskRiskLinkConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import {
	setFromHistory,
	setRiskGptKnowledgeBase,
	setRiskGptKnowledgeBaseOptions,
	setToastMessage,
} from 'common/store/reducer/commonReducer';

import { getKnowledgeBaseOptions } from '../api';
import { IKnowledgeBaseOptions } from '../api/type-def';

import styles from './KnowledgeBase.module.scss';

interface IKnowledgeBaseProps {
	tabValue: string;
	disabled: boolean;
	sessionId: string;
	messagesCount: number;
}

const KnowledgeBase = (props: IKnowledgeBaseProps) => {
	const { tabValue, disabled, messagesCount, sessionId } = props;
	const { OK } = HTTP_STATUS_CODES;
	const { OPTION_GENERAL } = KNOWLEDGE_BASE_OPTION_VALUE;
	const { OPTION_VALUE_GENERAL } = KNOWLEDGE_BASE_VALUE;
	const { closeIconColor } = styles;
	const [loading, setIsLoading] = useState<boolean>(false);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const dispatch = useDispatch();
	const riskGPTKnowledgeOptions = useSelector(
		(state: RootState) => state.common.riskGptKnowledgeBaseOptions,
	);
	useEffect(() => {
		if (riskGPTKnowledgeOptions.length === 0) {
			fetchKnowledgeBaseOption();
		}
	}, []);
	const defaultOptions = [
		{
			value: OPTION_VALUE_GENERAL,
			label: OPTION_GENERAL,
		},
	];
	const dropdownOptions =
		riskGPTKnowledgeOptions.length > 0
			? riskGPTKnowledgeOptions
			: defaultOptions;

	const fetchKnowledgeBaseOption = async () => {
		setIsLoading(true);
		const response = await getKnowledgeBaseOptions();
		setIsLoading(false);
		if (response?.statusCode === OK && response.responseBody) {
			const options = response.responseBody as IKnowledgeBaseOptions[];
			if (options.length) {
				dispatch(setRiskGptKnowledgeBaseOptions(options));
				dispatch(setRiskGptKnowledgeBase(options[0].value));
			}
		} else {
			dispatch(
				setToastMessage({
					message: KNOWLEDGE_BASE_ERROR,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const riskGptKnowledgeBase = useSelector(
		(state: RootState) => state.common.riskGptKnowledgeBase,
	);

	const fromHistory = useSelector(
		(state: RootState) => state.common.fromHistory,
	);

	const [open, setIsOpen] = useState<boolean>(false);

	const { CHAT } = TAB_VALUES;

	const handleClose = () => {
		setIsOpen(false);
	};

	const handleOpen = () => {
		setIsOpen(true);
		dispatch(setFromHistory(false));
		setTimeout(handleClose, 5000);
	};

	useEffect(() => {
		if (messagesCount === 1 && !fromHistory) {
			handleOpen();
		}
	}, [sessionId, messagesCount, fromHistory]);

	return (
		<Grid item display="flex">
			{tabValue === CHAT ? (
				<>
					{loading && <Loader show />}{' '}
					{!loading && (
						<Tooltip
							title={
								<Typography fontSize={14}>
									Start a new chat in order to select a different knowledge
									base.
								</Typography>
							}
							open={open}
							placement="right"
						>
							<Box>
								<SelectDropdown
									inputProps={{
										'data-testid': 'knowledge-base-options',
									}}
									label="Knowledge base"
									name="Knowledge base"
									id="Knowledge base"
									options={dropdownOptions}
									className={styles['select-knowledge-base']}
									value={riskGptKnowledgeBase || OPTION_VALUE_GENERAL}
									disabled={disabled}
									onChange={(value) => {
										dispatch(setRiskGptKnowledgeBase(value));
									}}
								/>
							</Box>
						</Tooltip>
					)}
				</>
			) : (
				<>
					<Box className={styles['ask-risk-gpt-icon']}>
						<CommentIcon fill={closeIconColor} />
					</Box>
					<Typography className={styles['ask-risk-link']}>
						Ask Risk Link
					</Typography>
				</>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</Grid>
	);
};

export default KnowledgeBase;
